/**
 * Pixel Manager Cookie Consent API
 **/

const consentStorageKey = "pmw_cookie_consent";

/**
 * Accept consent for all cookies
 *
 * @param duration {number|null}
 * @param source {"api"|"cmp"} - "cmp" marks a state relayed from a third-party
 *        CMP, which PMW must not store. See forgetConsentState().
 * @returns {void}
 * @public
 */
export const acceptAll = ({duration = null, source = "api"} = {}) => {

	const data = {
		statistics : true,
		marketing  : true,
		preferences: true,
		necessary  : true,
		duration   : duration,
	};

	saveState(data, source);
	loadApprovedPixels();
	document.dispatchEvent(new CustomEvent("pmw:consent:update", {detail: data}));
};

/**
 * Revoke consent for all cookies
 *
 * @param duration {number|null}
 * @param source {"api"|"cmp"}
 * @returns {void}
 * @public
 */
export const revokeAll = ({duration = null, source = "api"} = {}) => {

	const data = {
		statistics : false,
		marketing  : false,
		preferences: false,
		necessary  : true,
		duration   : duration,
	};

	saveState(data, source);
	document.dispatchEvent(new CustomEvent("pmw:consent:update", {detail: data}));
};

/**
 * Accept consent selectively
 *
 * @param statistics
 * @param marketing
 * @param preferences
 * @param necessary
 * @param duration
 * @param source {"api"|"cmp"}
 * @returns {void}
 * @public
 */
export const updateSelectively = ({
									  statistics = pmw.consent.categories.get().statistics,
									  marketing = pmw.consent.categories.get().marketing,
									  preferences = pmw.consent.categories.get().preferences,
									  necessary = pmw.consent.categories.get().necessary,
									  duration = null,
									  source = "api",
								  }) => {

	if (statistics === undefined || marketing === undefined || preferences === undefined || necessary === undefined) {
		console.log("pmw.consent.api.updateSelectively: It is recommended to pass all consent types. - statistics, marketing, preferences");
	}

	let data = {
		statistics,
		marketing,
		preferences,
		necessary,
		duration,
	};

	saveState(data, source);
	loadApprovedPixels();
	document.dispatchEvent(new CustomEvent("pmw:consent:update", {detail: data}));
};

/**
 * Set a cookie called pmw_cookie_consent with the value of pmw_cookie_consent
 * and set the default expiration date to 1 year from now.
 *
 * @param statistics {boolean}
 * @param marketing {boolean}
 * @param preferences {boolean}
 * @param necessary {boolean}
 * @param duration {number|null}
 *
 * @returns {void}
 * @private
 *
 * TODO: Use the data store
 * TODO: Cleanup
 */
const setConsentCookie = ({statistics, marketing, preferences, necessary, duration = null}) => {

	const data = {statistics, marketing, preferences, necessary};

	// If no duration is set, use the wpm storage,
	// otherwise use pmw.setCookie()
	if (duration === null) {
		pmw.storeData(consentStorageKey, data, true);
		return;
	}

	pmw.setCookie(consentStorageKey, JSON.stringify(data), duration);
};

/**
 * Drop PMW's own stored consent state.
 *
 * A state relayed from a third-party CMP must not be stored. PMW's record has no
 * expiry, lives in window.localStorage where clearing cookies does not reach it,
 * and is read before any CMP at boot. A stored grant would therefore outlive the
 * CMP's own decision and keep granting after the CMP has none, for example after
 * the visitor cleared their cookies, after Complianz cleared its category cookies
 * for a new cookie policy revision, or after the CMP's consent expired. The CMP
 * owns the decision and every CMP integration reads it back through getConsent()
 * on the next page load, so there is nothing to keep here. Records written by
 * earlier versions are cleaned up on the first relayed update.
 *
 * @returns {void}
 * @private
 */
const forgetConsentState = () => {

	if (pmw.retrieveData(consentStorageKey, true) !== undefined) {
		pmw.deleteDataKey(consentStorageKey, true);
	}

	if (pmw.getCookie(consentStorageKey)) {
		pmw.deleteCookie(consentStorageKey);
	}
};

const loadApprovedPixels = () => {
	window.pmw?.bus?.emitAndDispatch("pmw:load-pixels");
};

/**
 * Save the consent in the settings and set the cookie
 *
 * @param data
 * @param source {"api"|"cmp"}
 * @returns {void}
 * @private
 */
const saveState = (data, source = "api") => {

	pmw.consent.categories.set(data);

	source === "cmp" ? forgetConsentState() : setConsentCookie(data);

	pmw.console.log(`Updated consent state (source: ${source})`, data);
};

/**
 * Process the consent update from the external source
 *
 * @param attributes
 * @returns {void}
 * @public
 */
export const processExternalGcmConsentUpdate_experimental = (attributes) => {

	if (attributes[0] !== "consent") return;
	if (attributes[1] !== "update") return;
	if (attributes[2]?.source === "pmw") return;

	pmw.console.log("processExternalGcmConsentUpdate", attributes);

	let previousData = pmw.consent.categories.get();

	let data = {
		statistics : attributes[2].analytics_storage !== undefined ? attributes[2].analytics_storage === "granted" : previousData.statistics,
		marketing  : attributes[2].ad_storage !== undefined ? attributes[2].ad_storage === "granted" : previousData.marketing,
		preferences: attributes[2].functionality_storage !== undefined ? attributes[2].functionality_storage === "granted" : previousData.preferences,

		/**
		 * security_storage is not PMW's necessary category. A CMP denies it for
		 * its own reasons, and deriving necessary from it landed on
		 * necessary: false, which is a state PMW never sets itself. Necessary is
		 * granted unless something denies it through our own API.
		 *
		 * @since 1.67.1
		 */
		necessary  : previousData.necessary,
	};

	pmw.console.log("Consent state updated by external source (non-PMW gtag consent update)", data);

	/**
	 * The update belongs to whoever issued it, which is a CMP on the page, so it
	 * is applied but not stored. PMW's own record has no expiry, lives in
	 * localStorage where clearing cookies does not reach it, and is read before
	 * any CMP at boot. Storing a foreign update therefore outlived the banner
	 * that issued it: a decline kept declining after the CMP itself had no
	 * decision left, and a grant kept granting. Ticket 3437566303.
	 *
	 * @since 1.67.1
	 */
	saveState(data, "cmp");
	loadApprovedPixels();
};

export const getConsentStateFor = (category) => {
	return pmw.consent.categories.get()[category];
};

